import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { guardSurface } from '@/lib/platform/surfaces';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import type { AppEntry } from '@/app/routes-types';
import { LandingPage } from '@/features/auth/LandingPage';
import { SignInPage } from '@/features/auth/SignInPage';
import { SignUpPage } from '@/features/auth/SignUpPage';
import { ClientRegisterPage } from '@/features/client/ClientRegisterPage';
import { ClientsPage } from '@/features/client/ClientsPage';
import { TaxRulesPage } from '@/features/taxruleset/TaxRulesPage';
import { FilingCredentialsPage } from '@/features/filingcredential/FilingCredentialsPage';
import { FilingsPage } from '@/features/filing/FilingsPage';
import { FilingReviewPage } from '@/features/filing/FilingReviewPage';
import { ApprovalQueuePage } from '@/features/approval/ApprovalQueuePage';
import { FilingStatusPage } from '@/features/filing/FilingStatusPage';
import { NotFoundPage } from '@/components/NotFoundPage';

export const entry: AppEntry = { byRole: { 'actor-tax-preparer': '/clients', 'actor-firm-owner': '/client-register' } };
const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFoundPage });
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
  staticData: { title: 'FiduciaryFile', page: 'landing', standalone: true, isPublic: true },
});
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInPage,
  staticData: { title: 'Sign in', page: 'sign-in', standalone: true, isPublic: true },
});
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  component: SignUpPage,
  staticData: { title: 'Create account', page: 'sign-up', standalone: true, isPublic: true },
});
const clientRegisterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/client-register',
  component: ClientRegisterPage,
  beforeLoad: () => guardSurface(entry, SURFACES['client-register']),
  staticData: { title: 'Client register', page: SURFACES['client-register'], standalone: false, isPublic: false },
});
const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients',
  component: ClientsPage,
  beforeLoad: () => guardSurface(entry, SURFACES.clients),
  staticData: { title: 'Clients', page: SURFACES.clients, standalone: false, isPublic: false },
});
const taxRulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tax-rules',
  component: TaxRulesPage,
  beforeLoad: () => guardSurface(entry, SURFACES['tax-rules']),
  staticData: { title: 'Tax rules', page: SURFACES['tax-rules'], standalone: false, isPublic: false },
});
const credentialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/filing-credentials',
  component: FilingCredentialsPage,
  beforeLoad: () => guardSurface(entry, SURFACES['filing-credentials']),
  staticData: { title: 'Filing credentials', page: SURFACES['filing-credentials'], standalone: false, isPublic: false },
});
const filingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/filings',
  component: FilingsPage,
  beforeLoad: () => guardSurface(entry, SURFACES.filings),
  staticData: { title: 'Filings', page: SURFACES.filings, standalone: false, isPublic: false },
});
const reviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/filings/$filingId',
  component: FilingReviewPage,
  beforeLoad: () => guardSurface(entry, SURFACES['filing-review']),
  staticData: { title: 'Filing review', page: SURFACES['filing-review'], standalone: false, isPublic: false },
});
const approvalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/approval-queue',
  component: ApprovalQueuePage,
  beforeLoad: () => guardSurface(entry, SURFACES['approval-queue']),
  staticData: { title: 'Approval queue', page: SURFACES['approval-queue'], standalone: false, isPublic: false },
});
const statusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/filing-status',
  component: FilingStatusPage,
  beforeLoad: () => guardSurface(entry, SURFACES['filing-status']),
  staticData: { title: 'Filing status', page: SURFACES['filing-status'], standalone: false, isPublic: false },
});
const routeTree = rootRoute.addChildren([
  landingRoute,
  signInRoute,
  signUpRoute,
  clientRegisterRoute,
  clientsRoute,
  taxRulesRoute,
  credentialsRoute,
  filingsRoute,
  reviewRoute,
  approvalRoute,
  statusRoute,
]);
export const router = createRouter({ routeTree });
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
